import imgEmptyNoData from 'assets/images/img_emptyview_no_data.png';
import ChargeHelper from 'features/Charge/chargeHelper';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSButton, {
    KTBSButtonColor,
    KTBSButtonSize,
} from 'general/components/OtherKeenComponents/KTBSButton';
import KTCheckbox, { KTCheckboxType } from 'general/components/OtherKeenComponents/KTFormControls/KTCheckbox';
import AppColors from 'general/constants/AppColors';
import AppRoute from 'general/constants/AppRoute';
import UIHelper from 'general/helpers/UIHelper';
import StringUtils from 'general/utils/StringUtils';
import Utils from 'general/utils/Utils';
import useOnClickOutside from 'hooks/useOnClickOutside';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const CustomDropdownMultiChoiceDirections = {
    left: 'dropleft',
    up: 'dropup',
    right: 'dropright',
    down: 'dropdown',
};

export const CustomDropdownMultiChoiceAlignments = {
    start: 'dropdown-menu-start',
    end: 'dropdown-menu-end',
};

export const CustomDropdownMultiChoiceAutoCloseBehavior = {
    true: 'true',
    false: 'false',
    inside: 'inside',
    outside: 'outside',
};

CustomDropdownMultiChoiceView.propTypes = {
    name: PropTypes.string,
    toggleWrapperClassName: PropTypes.string,
    toggleElement: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
    dropdownMenuClassName: PropTypes.string,
    dropdownMenuStyle: PropTypes.object,
    dropdownMenuItems: PropTypes.array,
    selectedValue: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object])),
    direction: PropTypes.oneOf(Object.values(CustomDropdownMultiChoiceDirections)),
    alignment: PropTypes.oneOf(Object.values(CustomDropdownMultiChoiceAlignments)),
    offset: PropTypes.object,
    staticDisplay: PropTypes.bool, // on | off dynamic positioning
    autoCloseBehavior: PropTypes.oneOf(Object.values(CustomDropdownMultiChoiceAutoCloseBehavior)),
    contentEl: PropTypes.element,
    onChange: PropTypes.func,
    text: PropTypes.string,

    enableCheckValid: PropTypes.bool,
    isTouched: PropTypes.bool,
    isValid: PropTypes.bool,
    feedbackText: PropTypes.string,

    searchable: PropTypes.bool,
    footerEl: PropTypes.element,

    inputCheckbox: PropTypes.func,
    handleItemClick: PropTypes.func,

    disabled: PropTypes.bool,
    isDropdownForm: PropTypes.bool,
    id: PropTypes.string,

    liveSearch: PropTypes.bool,
    onTextChanged: PropTypes.func,
    defaultValue: PropTypes.object,
    defaultItem: PropTypes.object,
    appendText: PropTypes.string,
    agencyRole: PropTypes.object,
    hasButtonAdd: PropTypes.bool,
    titleType: PropTypes.string,
    subTitleType: PropTypes.string,
    ignoreConfirmClick: PropTypes.bool,
    idCheckType: PropTypes.string,
    handleCheckInput: PropTypes.func,
};

CustomDropdownMultiChoiceView.defaultProps = {
    name: '',
    toggleWrapperClassName: '',
    toggleElement: <></>,
    dropdownMenuClassName: '',
    dropdownMenuStyle: {},
    dropdownMenuItems: [],
    selectedValue: [],
    direction: CustomDropdownMultiChoiceDirections.down,
    alignment: CustomDropdownMultiChoiceAlignments.start,
    offset: null,
    staticDisplay: true,
    autoCloseBehavior: CustomDropdownMultiChoiceAutoCloseBehavior.true,
    contentEl: null,
    onChange: null,
    text: '',

    enableCheckValid: false,
    isValid: true,
    isTouched: false,
    feedbackText: '',
    searchable: false,
    footerEl: null,
    inputCheckbox: null,
    handleItemClick: null,
    disabled: false,
    isDropdownForm: false,
    id: '',
    liveSearch: false,
    onTextChanged: null,
    defaultValue: {},
    defaultItem: {},
    appendText: '',
    agencyRole: {},
    hasButtonAdd: false,
    titleType: '',
    subTitleType: '',
    ignoreConfirmClick: false,
    idCheckType: '',
    handleCheckInput: null,
};

/**
 *
 * @param {{
 * name: string,
 * toggleWrapperClassName: string,
 * toggleElement: element|function,
 * dropdownMenuClassName: string,
 * dropdownMenuStyle: object,
 * dropdownMenuItems: object[],
 * selectedValue: string|number|object,
 * direction: string,
 * alignment: string,
 * offset: {},
 * staticDisplay: boolean,
 * autoCloseBehavior: boolean,
 * contentEl: element,
 * onChange: function,
 * text: string,
 *
 * enableCheckValid: boolean,
 * isValid: boolean,
 * isTouched: boolean,
 * feedbackText: string,
 *
 * searchable: boolean,
 * footerEl: element,
 * inputCheckbox:func,
 * handleItemClick:func,
 * disabled:boolean,
 * isDropdownForm:boolean,
 * id: string,
 *
 * liveSearch: boolean,
 * onTextChanged: function,
 * defaultValue: object,
 * defaultItem: object,
 * appendText: string,
 * agencyRole: object,
 * hasButtonAdd: boolean,
 * titleType: string,
 * subTitleType: string,
 * ignoreConfirmClick: boolean,
 * idCheckType: string,
 * handleCheckInput: func,
 *
 * }} props
 * @returns
 */
function CustomDropdownMultiChoiceView(props) {
    // MARK: --- Params ---
    const {
        name,
        toggleWrapperClassName,
        toggleElement,
        dropdownMenuClassName,
        dropdownMenuStyle,
        dropdownMenuItems,
        selectedValue,
        direction,
        alignment,
        offset,
        staticDisplay,
        autoCloseBehavior,
        contentEl,
        onChange,
        text,

        enableCheckValid,
        isValid,
        isTouched,
        feedbackText,

        searchable,
        footerEl,
        inputCheckbox,
        handleItemClick,
        disabled,
        isDropdownForm,
        searchFormClassName,
        isOpen,
        id,
        liveSearch,
        onTextChanged,
        getSubItems,
        defaultValue,
        defaultItem,
        appendText,
        agencyRole,
        hasButtonAdd,
        titleType = 'name',
        subTitleType = 'email',
        ignoreConfirmClick,
        idCheckType = 'id',
        handleCheckInput,
        onSave,
    } = props;
    const currentSelectedValue = useRef(selectedValue ?? []);
    const showError = enableCheckValid && !isValid && isTouched && !_.isEmpty(feedbackText);
    const [showSubItem, setShowSubItem] = useState(false);
    const ref = useRef();
    const currentSelectedItem = useRef(null);
    const lastFetchedDefaultValue = useRef(null);

    const [searchText, setSearchText] = useState('');
    const { t } = useTranslation();
    const [filteredDropdownMenuItems, setFilteredDropdownMenuItems] = useState([]);
    const [updateTrigger, setUpdateTrigger] = useState(0);

    useEffect(() => {
        currentSelectedValue.current = selectedValue;
    }, [selectedValue]);

    useEffect(() => {
        if (currentSelectedItem.current && currentSelectedItem.current.role === defaultItem?.role) {
            if (!searchText?.length) {
                if (
                    !currentSelectedItem.current.subItems?.some(
                        (item) => JSON.stringify(item) === JSON.stringify(defaultItem?.item)
                    )
                ) {
                    currentSelectedItem.current = {
                        ...currentSelectedItem.current,
                        subItems: [defaultItem?.item, ...(currentSelectedItem.current.subItems || [])].filter(
                            Boolean
                        ),
                    };
                }
            } else {
                const itemName = StringUtils.removeVietnameseTones(
                    defaultItem?.item?.name ?? ''
                ).toLowerCase();
                if (
                    itemName?.includes(StringUtils.removeVietnameseTones(searchText ?? '').toLowerCase()) &&
                    !currentSelectedItem.current.subItemsSearch?.some(
                        (item) => JSON.stringify(item) === JSON.stringify(defaultItem?.item)
                    )
                ) {
                    currentSelectedItem.current = {
                        ...currentSelectedItem.current,
                        subItemsSearch: [
                            defaultItem?.item,
                            ...(currentSelectedItem.current.subItemsSearch || []),
                        ].filter(Boolean),
                    };
                }
            }
        }
    }, [defaultItem, searchText, currentSelectedItem.current]);

    useEffect(() => {
        const filteredOptions = searchText?.length
            ? currentSelectedItem.current?.subItemsSearch || []
            : currentSelectedItem.current?.subItems || [];
        setFilteredDropdownMenuItems(filteredOptions);
    }, [searchText, currentSelectedItem.current, updateTrigger]);

    const listRef = useRef(null);
    const isFetching = useRef(false);
    const [page, setPage] = useState(1);
    const lastScrollTop = useRef(0);

    const handleScroll = (item) => {
        if (!listRef.current || isFetching.current) return;

        const { scrollTop, scrollHeight, clientHeight } = listRef.current;

        if (scrollTop + clientHeight >= scrollHeight - 2) {
            const currentLength = searchText?.length
                ? currentSelectedItem.current?.subItemsSearch?.length || 0
                : currentSelectedItem.current?.subItems?.length || 0;
            const total = searchText?.length
                ? currentSelectedItem.current?.subItemsSearchTotal || 0
                : currentSelectedItem.current?.subItemsTotal || 0;

            if (currentLength < total) {
                isFetching.current = true;
                setPage((prev) => prev + 1);
                getSubItems?.({ ...currentSelectedItem.current, page, q: searchText })
                    .then((result) => {
                        if (result && result.values && typeof result.total !== 'undefined') {
                            const { values, total } = result;
                            currentSelectedItem.current = {
                                ...currentSelectedItem.current,
                                subItems: !searchText?.length ? [...(currentSelectedItem.current?.subItems || []), ...values] : [...(currentSelectedItem.current?.subItems || [])],
                                subItemsSearch: searchText?.length ? [...(currentSelectedItem.current?.subItemsSearch || []), ...values] : [...(currentSelectedItem.current?.subItemsSearch || [])],
                            };
                            setUpdateTrigger((prev) => prev + 1);

                            isFetching.current = false;
                        } else {
                            isFetching.current = false;
                        }
                    })
                    .catch((error) => {
                        console.error('Error fetching subitems:', error);
                        isFetching.current = false;
                    });
            }
        }

        lastScrollTop.current = scrollTop;
    };

    useEffect(() => {
        const element = listRef.current;
        if (element) {
            element.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (element) {
                element.removeEventListener('scroll', handleScroll);
            }
        };
    }, [searchText]);

    useEffect(() => {
        const shouldFetch =
            defaultValue?.role &&
            getSubItems &&
            JSON.stringify(lastFetchedDefaultValue.current) !== JSON.stringify(defaultValue);

        if (shouldFetch) {
            getSubItems(defaultValue)
                .then((result) => {
                    if (result && result.values && typeof result.total !== 'undefined') {
                        const { values, total } = result;

                        currentSelectedItem.current = {
                            ...defaultValue,
                            subItems: values,
                            subItemsTotal: total,
                        };
                        setFilteredDropdownMenuItems(values);
                        setShowSubItem(true);
                    }
                })
                .catch((error) => {
                    console.error('Error fetching default subitems:', error);
                });

            lastFetchedDefaultValue.current = defaultValue;
        }
    }, [defaultValue, getSubItems]);

    const handleNavigateAddAgency = (role) => {
        switch (role) {
            case 'SALE':
                return Utils.openInNewTab(`${AppRoute.saleList}/add`);
            case 'INVESTOR':
                return Utils.openInNewTab(`${AppRoute.investorList}/add`);
            case 'GROUP':
                return Utils.openInNewTab(`${AppRoute.groupList}/add`);
            default:
                return;
        }
    };

    const toggleCheckItem = (item) => {
        if (!item?.[idCheckType]) return;

        const currentItems = currentSelectedValue.current || [];
        let newCheckedItems;

        if (currentItems.some((selected) => selected?.[idCheckType] === item[idCheckType])) {
            newCheckedItems = currentItems.filter((selected) => selected?.[idCheckType] !== item[idCheckType]);
        } else {
            if (handleCheckInput) {
                if (!handleCheckInput(item)) {
                    return;
                }
                newCheckedItems = [...currentItems, item];
            } else {
                newCheckedItems = [...currentItems, item];
            }
        }

        currentSelectedValue.current = newCheckedItems;
        onChange?.(newCheckedItems);
        if (ignoreConfirmClick) {
            onSave?.(newCheckedItems);
        }
    };

    const renderDropdownMenuItems = useMemo(() => {
        if (filteredDropdownMenuItems.length > 0) {
            return (
                <>
                    {filteredDropdownMenuItems.map((item, index) => {
                        const isSelected = selectedValue?.some(
                            (selected) => selected?.[idCheckType] === item?.[idCheckType]
                        );
                        return (
                            <div key={index}>
                                <a
                                    className="d-flex gap-3 align-items-center justify-content-between dropdown-item px-5"
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        toggleCheckItem(item);
                                    }}
                                >
                                    <div className="d-flex flex-column" style={{ overflow: 'hidden' }}>
                                        <span
                                            style={{
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {!!titleType ? item?.[titleType] : item?.name}
                                        </span>
                                        {!!subTitleType && item?.[subTitleType] && (
                                            <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[600])}>
                                                {item?.[subTitleType]}
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ zIndex: 100 }}>
                                        <KTCheckbox
                                            name={`checkbox-${index}`}
                                            additionalClassName="checkbox-success"
                                            type={KTCheckboxType.default}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                toggleCheckItem(item);
                                            }}
                                            checked={isSelected}
                                        />
                                    </div>
                                </a>
                                {item.showDivider && <div className="dropdown-divider" />}
                            </div>
                        );
                    })}
                    <div
                        className={`py-2 px-4 d-flex ${hasButtonAdd && currentSelectedItem.current?.role !== agencyRole
                            ? 'justify-content-between'
                            : 'justify-content-end'
                            }`}
                        style={{
                            position: 'sticky',
                            bottom: 0,
                            background: 'white',
                            zIndex: 1023,
                            width: '100%',
                        }}
                    >
                        {hasButtonAdd && currentSelectedItem.current?.role !== agencyRole && (
                            <KTBSButton
                                color={KTBSButtonColor.secondary}
                                title={t(
                                    `Thêm ${currentSelectedItem.current?.role === 'SALE'
                                        ? 'đại lý mới'
                                        : 'nhà đầu tư mới'
                                    }`
                                )}
                                size={KTBSButtonSize.large}
                                borderRadius={5}
                                buttonTitleFontWeight="600"
                                iconElement={<i className="fa-regular fa-circle-plus" style={{ fontSize: 15 }} />}
                                onPress={() => handleNavigateAddAgency(currentSelectedItem.current?.role)}
                                buttonTitleColor={AppColors.gray[800]}
                            />
                        )}
                        {!ignoreConfirmClick && (
                            <KTBSButton
                                color={KTBSButtonColor.success}
                                title={t('Lưu')}
                                size={KTBSButtonSize.large}
                                borderRadius={5}
                                buttonTitleFontWeight="600"
                                onPress={() => onSave?.(currentSelectedValue.current)}
                            />
                        )}
                    </div>
                </>
            );
        }

        return (
            <div className="mx-auto">
                <div className="d-flex flex-column align-items-center justify-content-center gap-4 h-100 py-3">
                    <img src={imgEmptyNoData} style={{ maxWidth: '150px', maxHeight: '150px' }} />
                    <div className="d-flex flex-column align-items-center gap-2">
                        <span
                            style={{
                                fontWeight: '700',
                                fontSize: Utils.convertToFontSize(14),
                                color: AppColors.gray[800],
                            }}
                        >
                            Chưa có dữ liệu hiển thị
                        </span>
                    </div>
                </div>
            </div>
        );
    }, [filteredDropdownMenuItems, currentSelectedValue.current, titleType, subTitleType, selectedValue]);

    return (
        <div id={`${id}`} ref={ref} className={`${!isDropdownForm ? 'd-flex' : ''}`}>
            <div className={`${direction} w-100 ${disabled && 'dropdown-disabled'}`}>
                {/* Toggle element */}
                {staticDisplay ? (
                    <div
                        className={`${toggleWrapperClassName} ${direction}`}
                        data-bs-toggle="dropdown"
                        data-bs-display="static"
                        aria-expanded="false"
                        data-bs-auto-close={autoCloseBehavior}
                        onClick={() => {
                            setPage(1);
                            setSearchText('');
                        }}
                        style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                            position: 'relative',
                        }}
                    >
                        {typeof toggleElement === 'function' ? toggleElement({ showError }) : toggleElement}
                    </div>
                ) : (
                    <div
                        className={`${toggleWrapperClassName} ${direction}`}
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        data-bs-auto-close={autoCloseBehavior}
                        style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                            position: 'relative',
                        }}
                    >
                        {typeof toggleElement === 'function' ? toggleElement({ showError }) : toggleElement}
                    </div>
                )}

                {/* Dropdown menu */}
                <div
                    className={`dropdown-menu ${dropdownMenuClassName} ${alignment} py-0`}
                    style={{
                        ...dropdownMenuStyle,
                        display: disabled ? 'none' : '',
                    }}
                    ref={listRef}
                    onScroll={handleScroll}
                >
                    {!showSubItem && (
                        <div className="py-2">
                            {!defaultValue?.role &&
                                dropdownMenuItems.length > 0 &&
                                !showSubItem &&
                                dropdownMenuItems.map((item, index) => {
                                    return (
                                        <div key={index}>
                                            <a
                                                className={`d-flex align-items-center justify-content-between dropdown-item 
                          ${currentSelectedValue.current?.[idCheckType] &&
                                                        currentSelectedValue.current?.[idCheckType] === item?.[idCheckType]
                                                        ? 'active'
                                                        : ''
                                                    } px-5`}
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setPage(1);
                                                    if (getSubItems)
                                                        getSubItems(item)
                                                            .then((result) => {
                                                                if (
                                                                    result &&
                                                                    result.values &&
                                                                    typeof result.total !== 'undefined'
                                                                ) {
                                                                    const { values, total } = result;

                                                                    currentSelectedItem.current = {
                                                                        ...item,
                                                                        subItems: values,
                                                                        subItemsTotal: total,
                                                                    };
                                                                    setShowSubItem(true);
                                                                }
                                                            })
                                                            .catch((error) => {
                                                                console.error('Error in handling sub items:', error);
                                                            });
                                                }}
                                            >
                                                <div className="d-flex flex-column">
                                                    <span>{item.name}</span>
                                                </div>

                                                <i className="far fa-angle-right text-success" />
                                            </a>
                                        </div>
                                    );
                                })}
                        </div>
                    )}

                    {showSubItem && (
                        <div className="pt-2">
                            <div className='position-sticky top-0 bg-white'
                                style={{
                                    zIndex: 1000
                                }}
                            >
                                {/* Title */}
                                {!defaultValue?.role && (
                                    <div className="dropdown-header d-flex align-items-center">
                                        <div
                                            style={{ cursor: 'pointer' }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setShowSubItem(false);
                                            }}
                                        >
                                            <i
                                                style={{ color: AppColors.gray[800] }}
                                                className="fas fa-arrow-left mr-3"
                                            ></i>
                                        </div>
                                        {!_.isEmpty(currentSelectedItem?.current?.name) && (
                                            <span
                                                style={{
                                                    fontSize: Utils.returnFontSize(16),
                                                    fontWeight: '600',
                                                    color: AppColors.gray[800],
                                                }}
                                            >
                                                Chọn{' '}
                                                {currentSelectedItem.current?.role === 'INVESTOR'
                                                    ? currentSelectedItem.current?.name
                                                    : Utils.lowercaseFirstChar(currentSelectedItem.current?.name)}{' '}
                                                {appendText}
                                            </span>
                                        )}
                                    </div>
                                )}

                                {searchable && (
                                    <div className={`${searchFormClassName ? searchFormClassName : 'px-4 py-2'}`}>
                                        <KTFormSearch
                                            name={`${name}_searchbar`}
                                            value={searchText}
                                            placeholder={`${t('TypeToSearch')}...`}
                                            onSubmit={(text) => {
                                                if (!liveSearch) {
                                                    if (getSubItems)
                                                        getSubItems({ ...currentSelectedItem.current, q: text }).then(
                                                            (result) => {
                                                                if (result && result.values && typeof result.total !== 'undefined') {
                                                                    const { values, total } = result;
                                                                    currentSelectedItem.current = {
                                                                        ...currentSelectedItem.current,
                                                                        subItemsSearch: values,
                                                                        subItemsSearchTotal: total,
                                                                    };
                                                                    setSearchText(text);
                                                                }
                                                            }
                                                        );
                                                } else {
                                                    if (onTextChanged) {
                                                        onTextChanged(text);
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                )}
                            </div>

                            {!_.isEmpty(contentEl) ? (
                                <div>{contentEl}</div>
                            ) : (
                                <div>{renderDropdownMenuItems}</div>
                            )}

                            {/* footer element */}
                            {footerEl && footerEl}
                        </div>
                    )}
                </div>
            </div>
            {!_.isEmpty(text) && <span className="form-text text-muted">{text}</span>}
        </div>
    );
}

export default CustomDropdownMultiChoiceView;
